import { useCallback, useEffect, useState } from 'react';
import { useAuthenticatedFetchWithWorkspace } from '../Keycloak/useAuthenticatedFetchWithWorkspace';

export type AuthFetch = ReturnType<typeof useAuthenticatedFetchWithWorkspace>['authFetch'];

export type ApiCallResult<T> = { ok: true; data: T } | { ok: false; message: string; cause: unknown };

export type ErrorMessageExtractor = (err: unknown, fallback: string) => string;

export const LD_JSON_HEADERS = { 'Content-Type': 'application/ld+json', Accept: 'application/ld+json' };
export const MERGE_PATCH_HEADERS = { 'Content-Type': 'application/merge-patch+json', Accept: 'application/ld+json' };

export const errorMessage: ErrorMessageExtractor = (err, fallback) =>
  err instanceof Error ? err.message : fallback;

export const parseErrorBody = (err: unknown): Record<string, unknown> | null => {
  if (!(err instanceof Error)) return null;
  const jsonStart = err.message.indexOf('{');
  if (jsonStart === -1) return null;
  try {
    const body: unknown = JSON.parse(err.message.slice(jsonStart));
    return body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
  } catch {
    return null;
  }
};

export const backendErrorMessage: ErrorMessageExtractor = (err, fallback) => {
  if (!(err instanceof Error)) return fallback;
  const body = parseErrorBody(err);
  const message = body?.error || body?.message;
  if (body) return typeof message === 'string' && message ? message : fallback;
  return err.message || fallback;
};

export const useApiCall = (toMessage: ErrorMessageExtractor = errorMessage) => {
  const { authFetch, isReady, workspaceId } = useAuthenticatedFetchWithWorkspace();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const run = useCallback(
    async <T>(request: () => Promise<T>, fallback: string): Promise<ApiCallResult<T>> => {
      setIsLoading(true);
      setError('');
      try {
        return { ok: true, data: await request() };
      } catch (err) {
        const message = toMessage(err, fallback);
        setError(message);
        return { ok: false, message, cause: err };
      } finally {
        setIsLoading(false);
      }
    },
    [toMessage],
  );

  return { authFetch, isReady, workspaceId, run, isLoading, error };
};

export type ApiLoader<T> = (authFetch: AuthFetch, workspaceId: string) => Promise<T>;

export const useApiResource = <T>(load: ApiLoader<T> | null, initialData: T, fallback: string) => {
  const { authFetch, isReady, workspaceId } = useAuthenticatedFetchWithWorkspace();
  const [state, setState] = useState({ data: initialData, loading: load !== null, error: '' });

  useEffect(() => {
    if (!load || !isReady || !workspaceId) return;
    let cancelled = false;
    setState(previous => ({ ...previous, loading: true, error: '' }));
    load(authFetch, workspaceId).then(
      data => {
        if (!cancelled) setState({ data, loading: false, error: '' });
      },
      err => {
        if (!cancelled) setState(previous => ({ ...previous, loading: false, error: errorMessage(err, fallback) }));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [load, isReady, workspaceId, authFetch, fallback]);

  return state;
};

export const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
